// Copyright 2025, University of Maryland, All Rights Reserved
import { useQuery } from '@tanstack/react-query'
import { createCostsApi, CostLevel } from '../services/costs-api'
import { useApi } from '../CostsComponent'
import type { YearlyCostsResponse } from '../types'

const getCurrentYear = () => new Date().getFullYear()

export const useCosts = (
  year: number = getCurrentYear(), 
  app_id: string = 'default-app', 
  selectedOrganizationId: string = 'demo-org', 
  selectedWorkspaceId: string = 'demo-workspace',
  level: CostLevel = 'app'
) => {
  const { apiInstance } = useApi();
  const costsApi = createCostsApi(apiInstance);

  const { data, isPending, isError } = useQuery({
    queryKey: ['costs', app_id, year, selectedOrganizationId, selectedWorkspaceId, level],
    queryFn: async () => {
      console.log('[useCosts] Fetching costs for year:', year, 'level:', level);
      if (!selectedOrganizationId || (level !== 'org' && !selectedWorkspaceId)) {
        throw new Error('Required organization or workspace not selected')
      }
      try {
        const costs = await costsApi.getYearlyCosts(
          app_id, 
          year, 
          selectedOrganizationId, 
          selectedWorkspaceId, 
          level
        )
        return costs
      } catch (error) {
        console.error('Failed to load costs:', error)
        throw error
      }
    },
    staleTime: 300000, // 5 minutes
    gcTime: 3600000,   // 1 hour
    enabled: !!selectedOrganizationId && (level === 'org' || !!selectedWorkspaceId)
  })

  return {
    costs: data || {} as YearlyCostsResponse,
    isLoading: isPending,
    error: isError ? 'Failed to load cost data' : null
  }
}